import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, AlertCircle, Flame, Snowflake, DollarSign, Users, Info } from 'lucide-react';

interface ConstellationItem {
  item_id: number;
  name: string;
  price: number;
  section: string;
  order_count: number;
  price_range: string;
  popularity: number;
  image_url?: string | null;
}

interface TopItem {
  name: string;
  price: number;
  order_count: number;
  revenue: number;
}

interface PriceRange {
  label: string;
  min_price: number;
  max_price: number;
  order_count: number;
  percentage: number;
  recommendation?: string;
  // Enhanced fields
  temperature?: 'on_fire' | 'hot' | 'warm' | 'cool';
  top_items?: TopItem[];
  avg_items_per_order?: number;
  insight?: string;
}

interface HeatGaugeMetersProps {
  items: ConstellationItem[];
  distribution: PriceRange[];
  comboInsights?: {
    [key: string]: {
      Entrees: Array<{ name: string; price: number; order_count: number }>;
      Appetizers: Array<{ name: string; price: number; order_count: number }>;
      Drinks: Array<{ name: string; price: number; order_count: number }>;
    };
  };
}

const HeatGaugeMeters: React.FC<HeatGaugeMetersProps> = ({ items, distribution, comboInsights }) => {
  const [selectedRange, setSelectedRange] = useState<string | null>(null);

  // Group items by price range
  const itemsByRange = items.reduce((acc, item) => {
    if (!acc[item.price_range]) acc[item.price_range] = [];
    acc[item.price_range].push(item);
    return acc;
  }, {} as Record<string, ConstellationItem[]>);

  // Sort items in each range by popularity
  Object.keys(itemsByRange).forEach(range => {
    itemsByRange[range].sort((a, b) => b.order_count - a.order_count);
  });

  // Calculate total revenue across all ranges for revenue percentage calculation
  const totalRevenue = distribution.reduce((sum, range) => {
    return sum + (range.order_count * ((range.min_price + range.max_price) / 2));
  }, 0);

  // Calculate temperature based on composite score (order % + revenue %) - fallback for old data
  const getTemperature = (range: PriceRange) => {
    // Use enhanced temperature if available
    if (range.temperature) {
      switch(range.temperature) {
        case 'on_fire': return 5;
        case 'hot': return 4;
        case 'warm': return 3;
        case 'cool': return 2;
        default: return 1;
      }
    }
    
    // Fallback calculation for old data
    if (range.order_count === 0) return 0;
    
    // Calculate this range's revenue
    const rangeRevenue = range.order_count * ((range.min_price + range.max_price) / 2);
    const revenuePercentage = (rangeRevenue / totalRevenue) * 100;
    
    // Composite score: 50% order frequency + 50% revenue contribution
    const compositeScore = (range.percentage * 0.5) + (revenuePercentage * 0.5);
    
    // Temperature thresholds based on composite score
    if (compositeScore < 2) return 1;   // Cold
    if (compositeScore < 10) return 2;  // Cool
    if (compositeScore < 20) return 3;  // Warm
    if (compositeScore < 30) return 4;  // Hot
    return 5; // On Fire!
  };

  // Get color based on temperature
  const getTemperatureColor = (temp: number) => {
    switch (temp) {
      case 0: return '#94a3b8'; // slate-400 (empty)
      case 1: return '#60a5fa'; // blue-400 (cold)
      case 2: return '#34d399'; // emerald-400 (cool)
      case 3: return '#fbbf24'; // yellow-400 (warm)
      case 4: return '#f97316'; // orange-500 (hot)
      case 5: return '#ef4444'; // red-500 (very hot)
      default: return '#94a3b8';
    }
  };

  // Get temperature label
  const getTemperatureLabel = (temp: number) => {
    switch (temp) {
      case 0: return 'Empty';
      case 1: return 'Cold';
      case 2: return 'Cool';
      case 3: return 'Warm';
      case 4: return 'Hot';
      case 5: return 'On Fire!';
      default: return 'Unknown';
    }
  };

  // Price range order
  const priceRangeOrder = ['$0-10', '$10-15', '$15-20', '$20-30', '$30-50', '$50-100', '$100+'];

  return (
    <div className="space-y-4">
      {/* Temperature Legend */}
      <div className="flex items-center justify-between mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-2">
          <Snowflake className="h-4 w-4 text-blue-500" />
          <span className="text-gray-600 text-sm">Cold = Low Impact</span>
        </div>
        <div className="text-gray-500 text-xs">Based on orders + revenue</div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600 text-sm">Hot = High Impact</span>
          <Flame className="h-4 w-4 text-red-500" />
        </div>
      </div>

      {/* Heat Gauge Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Sort by temperature (hottest first) then by price range for ties */}
        {distribution
          .sort((a, b) => {
            const tempA = getTemperature(a);
            const tempB = getTemperature(b);
            if (tempB !== tempA) return tempB - tempA; // Higher temperature first
            // For same temperature, sort by revenue
            const revA = a.order_count * ((a.min_price + a.max_price) / 2);
            const revB = b.order_count * ((b.min_price + b.max_price) / 2);
            return revB - revA;
          })
          .map(range => {
          const rangeLabel = range.label;
          const temperature = getTemperature(range);
          const color = getTemperatureColor(temperature);
          const tempLabel = getTemperatureLabel(temperature);
          const rangeItems = itemsByRange[rangeLabel] || [];
          const isSelected = selectedRange === rangeLabel;

          return (
            <motion.div
              key={rangeLabel}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              className="relative cursor-pointer"
              onClick={() => setSelectedRange(isSelected ? null : rangeLabel)}
            >
              <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-all shadow-sm hover:shadow-md">
                {/* Price Range Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-900 font-bold text-xl">{rangeLabel}</h3>
                  <div className="text-gray-500 text-sm font-medium">{range.order_count} orders</div>
                </div>

                {/* Temperature Gauge */}
                <div className="relative mb-4">
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(10, range.percentage * 2)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full rounded-full relative shadow-sm"
                      style={{ backgroundColor: color }}
                    >
                      {/* Animated glow effect for hot items */}
                      {temperature >= 4 && (
                        <motion.div
                          className="absolute inset-0 rounded-full"
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          style={{ 
                            background: `radial-gradient(circle, ${color}88 0%, transparent 70%)`,
                            filter: 'blur(4px)'
                          }}
                        />
                      )}
                    </motion.div>
                  </div>
                  
                  {/* Temperature Label */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-semibold" style={{ color }}>
                      {tempLabel}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 font-medium">
                        {range.percentage}%
                      </span>
                      {/* Show revenue indicator for high-revenue ranges */}
                      {(() => {
                        const rangeRevenue = range.order_count * ((range.min_price + range.max_price) / 2);
                        const revenuePercentage = (rangeRevenue / totalRevenue) * 100;
                        if (revenuePercentage > 20) {
                          return <span className="text-xs text-yellow-400" title={`${revenuePercentage.toFixed(1)}% of revenue`}>💰</span>;
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Top Items Section - Enhanced */}
                <div className="space-y-2">
                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">
                    TOP SELLERS
                  </p>

                  {/* Use enhanced top_items if available */}
                  {range.top_items && range.top_items.length > 0 ? (
                    <>
                      {range.top_items.slice(0, isSelected ? 5 : 3).map((item, index) => (
                        <motion.div
                          key={`${item.name}-${index}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between py-1"
                        >
                          <span className="text-gray-800 truncate flex-1 mr-2 text-sm font-medium">
                            {item.name}
                          </span>
                          <span className="text-gray-400 text-xs font-medium">
                            {item.order_count} orders
                          </span>
                        </motion.div>
                      ))}

                      {/* Show avg items per order if available */}
                      {range.avg_items_per_order && range.avg_items_per_order > 0 && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
                          <Users className="h-3 w-3" />
                          <span>Avg {range.avg_items_per_order.toFixed(1)} items/order</span>
                        </div>
                      )}
                    </>
                  ) : temperature === 0 || rangeItems.length === 0 ? (
                    // Empty state or no individual items in this range
                    <div className="space-y-2">
                      {/* Show combo insights for high-value ranges */}
                      {comboInsights && comboInsights[rangeLabel] && temperature > 0 ? (
                        <div className="space-y-2">
                          {/* Show top 1 from each category */}
                          {comboInsights[rangeLabel].Entrees[0] && (
                            <div className="flex items-center justify-between py-1">
                              <span className="text-gray-800 text-sm font-medium">
                                1. {comboInsights[rangeLabel].Entrees[0].name}
                              </span>
                              <span className="text-gray-400 text-xs font-medium">
                                ${comboInsights[rangeLabel].Entrees[0].price.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {comboInsights[rangeLabel].Appetizers[0] && (
                            <div className="flex items-center justify-between py-1">
                              <span className="text-gray-800 text-sm font-medium">
                                2. {comboInsights[rangeLabel].Appetizers[0].name}
                              </span>
                              <span className="text-gray-400 text-xs font-medium">
                                ${comboInsights[rangeLabel].Appetizers[0].price.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {comboInsights[rangeLabel].Drinks[0] && (
                            <div className="flex items-center justify-between py-1">
                              <span className="text-gray-800 text-sm font-medium">
                                3. {comboInsights[rangeLabel].Drinks[0].name}
                              </span>
                              <span className="text-gray-400 text-xs font-medium">
                                ${comboInsights[rangeLabel].Drinks[0].price.toFixed(2)}
                              </span>
                            </div>
                          )}

                        </div>
                      ) : (
                        <div className="text-gray-400 text-sm italic py-1">
                          {range.min_price >= 30 ? 'Order combinations' : 'No items yet'}
                        </div>
                      )}
                      
                      {/* Enhanced Insight/Info Section */}
                      {(range.insight || (temperature === 0 || !comboInsights || !comboInsights[rangeLabel])) && (
                        <div className="flex items-start gap-2 p-3 bg-[#FDB913]/10 rounded-lg border border-[#FDB913]/30 mt-3">
                          <Info className="h-4 w-4 text-[#FDB913] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[#b8860b] text-sm font-medium">
                              {temperature === 0 ? 'Opportunity!' : 'Info'}
                            </p>
                            <p className="text-gray-600 text-xs mt-1">
                              {range.insight || range.recommendation || 'Add items in this range to capture revenue'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Show top 3 items
                    <>
                      {rangeItems.slice(0, isSelected ? 5 : 3).map((item, index) => (
                        <motion.div
                          key={item.item_id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between py-1"
                        >
                          <span className="text-gray-800 truncate flex-1 mr-2 text-sm font-medium">
                            {index + 1}. {item.name}
                          </span>
                          <span className="text-gray-400 text-xs font-medium">
                            {item.order_count}
                          </span>
                        </motion.div>
                      ))}

                      {/* Show more items indicator if clicking would reveal more */}
                      {!isSelected && rangeItems.length > 3 && (
                        <p className="text-gray-400 text-xs mt-1 cursor-pointer hover:text-gray-600">
                          {rangeItems.length === 4 ? 'Click to see 1 more item' : 'Click to see more'}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Revenue Impact */}
                {temperature > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs font-medium uppercase tracking-wider">Revenue</span>
                      <span className="text-gray-900 font-bold text-base">
                        ${(range.order_count * ((range.min_price + range.max_price) / 2)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Action Items for Cold/Empty Ranges */}
                {temperature <= 1 && temperature > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-blue-700 text-xs font-medium">
                      💡 This range needs attention - consider promotions or new items
                    </p>
                  </div>
                )}
              </div>

              {/* Hot indicator flame */}
              {temperature >= 4 && (
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <div className="bg-red-500 rounded-full p-1.5">
                    <Flame className="h-4 w-4 text-white" />
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

    </div>
  );
};

export default HeatGaugeMeters;